<template>
  <Modal v-model="modelValue" :closeOnBackdropClick="false" class="modal-dialog-scrollable modal-xl">
    <template #content>
      <div class="modal-header">
        <div class="d-flex flex-row align-items-center">
          <IconHelpHexagonFilled class="info-help-icon" width="32" height="32" />
          <div class="d-flex flex-column">
            <div class="modal-title">Help & Guide</div>
            <div class="modal-subtitle">{{ selectedChunk.title }}</div>
          </div>
        </div>
        <button type="button" class="btn-close" @click="modelValue = false"></button>
      </div>
      <div
        v-if="!loadingHelpPages"
        class="modal-body"
        :style="{ gridTemplateColumns: gridColumns, height: modalBodyHeight }">
        <div class="body-sidepanel">
          <div
            v-for="chunk in help"
            :key="chunk.title"
            class="sidepanel-item"
            :class="{ selected: selectedChunk.title === chunk.title }"
            @click="selectedChunk = chunk">
            <div class="sidepanel-title">{{ chunk.title }}</div>
            <div class="sidepanel-description">{{ chunk.description }}</div>
          </div>
        </div>
        <div class="body-center-panel">
          <div class="center-title">{{ selectedChunk.title }}</div>
          <div class="center-description" v-html="selectedContent"></div>
        </div>
        <div v-show="selectedChunk.image" ref="imageElement" class="body-image-panel">
          <ImageExplainer
            :src="selectedChunk.image ?? ''"
            :rects="rects"
            :focusOn="currentFocus"
            @canvasResized="updateImageHeight"
            @imageLoaded="handleImageLoaded" />
        </div>
      </div>
      <!-- Skeleton loader -->
      <div v-else class="modal-body">
        <div class="body-sidepanel">
          <div v-for="(_, index) in 6" :key="index" class="sidepanel-item">
            <div class="placeholder-glow">
              <div class="sidepanel-title">
                <div class="placeholder col-9"></div>
              </div>
              <div class="sidepanel-description">
                <div class="placeholder col-11"></div>
                <div class="placeholder col-10"></div>
                <div class="placeholder col-8"></div>
              </div>
            </div>
          </div>
        </div>
        <div class="body-center-panel">
          <div class="placeholder-glow">
            <div class="center-title">
              <div class="placeholder col-11"></div>
            </div>
            <div class="center-description">
              <div class="placeholder col-11"></div>
              <div class="placeholder col-10"></div>
              <div class="placeholder col-8"></div>
              <br />
              <br />
              <div class="placeholder col-11"></div>
              <div class="placeholder col-10"></div>
              <div class="placeholder col-8"></div>
            </div>
          </div>
        </div>
        <div class="body-image-panel" style="padding: 30px">
          <div class="placeholder-glow">
            <div class="ratio ratio-1x1 placeholder">
              <div class="placeholder-image"></div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { ref, onMounted, watch, computed, nextTick } from 'vue';
import { IconHelpHexagonFilled } from '@tabler/icons-vue';
import { Modal } from '@prospective/lithium';
import ImageExplainer from '../common/ImageExplainer.vue';
import { fetchHelpPages } from '@/api/campaign';
import { marked } from 'marked';

marked.setOptions({
  gfm: true,
  breaks: true,
});

type HelpChunk = {
  title: string;
  description: string;
  content: string;
  context: string;
  image?: string;
  focus: {
    height: number;
    width: number;
    x: number;
    y: number;
  };
  sequence: number;
};

const modelValue = defineModel('modelValue', {
  type: Boolean,
  default: false,
});

const props = defineProps<{
  context: string;
}>();

const loadingHelpPages = ref(false);
const help = ref<HelpChunk[]>([]);
const selectedChunk = ref<HelpChunk>({
  title: '',
  description: '',
  content: '',
  context: '',
  image: '',
  focus: { height: 0, width: 0, x: 0, y: 0 },
  sequence: 0,
});

// Counter to ensure unique focus keys on every change
const focusChangeCounter = ref(0);
const lastImageHeight = ref<number | null>(null);
const imageElement = ref<HTMLElement | null>(null);
const isImageLoaded = ref(false);

const rects = computed<Record<string, { x: number; y: number; w: number; h: number }>>(() => {
  if (!selectedChunk.value.focus) {
    return {};
  }
  const key = `focus-${focusChangeCounter.value}`;
  return {
    [key]: {
      x: selectedChunk.value.focus.x,
      y: selectedChunk.value.focus.y,
      w: selectedChunk.value.focus.width,
      h: selectedChunk.value.focus.height,
    },
  };
});

// The focus key should match the rect key
const currentFocus = computed<string | undefined>(() => {
  if (help.value.length === 0 || !selectedChunk.value.focus) {
    return undefined;
  }
  return `focus-${focusChangeCounter.value}`;
});

const selectedContent = computed(() => {
  let rawContent = selectedChunk.value.content;

  // If content is wrapped in pre tags, extract the inner content - it comes that way from the API
  if (rawContent.startsWith('<pre>') && rawContent.endsWith('</pre>')) {
    rawContent = rawContent.slice(5, -6).trim();
  }

  const content = marked.parse(rawContent);
  return content;
});

const handleClickOutside = (event: MouseEvent) => {
  const modalElement = document.querySelector('.body-sidepanel');
  // if the click is in the sidepanel, do nothing
  if (modalElement && modalElement.contains(event.target as Node)) {
    return;
  }
};

const updateImageHeight = () => {
  if (selectedChunk.value.image && imageElement.value) {
    const lastHeight = imageElement.value.clientHeight;
    nextTick(() => {
      lastImageHeight.value = lastHeight + 5; // offset
    });
  }
};

const handleImageLoaded = () => {
  isImageLoaded.value = true;
  // Trigger focus animation now that image is loaded
  if (selectedChunk.value.focus && focusChangeCounter.value === 0) {
    focusChangeCounter.value++;
  }
};

watch(selectedChunk, () => {
  focusChangeCounter.value = 0;

  nextTick(() => {
    // Only increment if image is already loaded (for subsequent changes)
    if (isImageLoaded.value && selectedChunk.value.focus) {
      focusChangeCounter.value++;
    }
  });
});

watch(
  () => modelValue.value,
  (newValue) => {
    if (newValue) {
      loadingHelpPages.value = true;
      fetchHelpPages(props.context)
        .then((data) => {
          help.value = data.data;

          if (help.value.length > 0) {
            selectedChunk.value = help.value[0];
            // Don't increment counter here - wait for imageLoaded event
          }
        })
        .finally(() => {
          loadingHelpPages.value = false;
        });
    } else {
      lastImageHeight.value = null;
      focusChangeCounter.value = 0;
      isImageLoaded.value = false;
    }
  },
  { immediate: true }
);

const gridColumns = computed(() => {
  return selectedChunk.value.image ? '1fr 2fr 2fr' : '1fr 4fr';
});

const modalBodyHeight = computed(() => {
  if (loadingHelpPages.value) {
    return '600px';
  }

  // If current chunk has an image
  if (selectedChunk.value.image) {
    // Use measured height if available AND reasonable, otherwise default
    if (lastImageHeight.value !== null && lastImageHeight.value > 100) {
      return `${lastImageHeight.value}px`;
    }
    return '600px';
  }

  // No image: use last measured height to prevent collapse, or auto if none exists
  if (lastImageHeight.value !== null && lastImageHeight.value > 100) {
    return `${lastImageHeight.value}px`;
  }

  return 'auto';
});

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});
</script>

<style scoped lang="scss">
:deep(.modal-content) {
  box-shadow: 0px 0px 23.4px 0px #0000003d;
  border-left: 1px solid #eaecf0;
}

.modal-header {
  padding: 20px 16px;
  height: 74px;
  display: flex;
  align-items: center;

  .info-help-icon {
    margin-right: 16px;
  }

  .modal-title {
    font-size: 16px;
    line-height: 24px;
    font-weight: 600;
    color: #101828;
  }

  .modal-subtitle {
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    color: #475467;
  }

  .btn-close {
    position: relative;
  }
}

.modal-body {
  display: grid;
  gap: 0;
  padding: 0;
  transition: height 0.2s ease-in-out;
  overflow: hidden;

  .body-sidepanel {
    display: flex;
    flex-direction: column;
    margin-right: 20px;
    border-right: 1px solid #eaecf0;
    overflow-y: auto;
    max-height: 100%;

    .sidepanel-item {
      padding: 10px 20px;
      border-bottom: 1px solid #eaecf0;
      cursor: pointer;

      &.selected {
        background-color: color-mix(in srgb, var(--tw-color-primary) 8%, transparent);
      }

      .sidepanel-title {
        font-size: 13px;
        font-weight: 500;
        color: #344054;
        margin-bottom: 6px;
        line-height: 20px;
      }

      .sidepanel-description {
        font-size: 12px;
        font-weight: 500;
        color: #8e939d;
        line-height: 17px;
      }
    }
  }

  .body-center-panel {
    padding: 30px 20px;
    border-right: 1px solid #eaecf0;
    overflow-y: auto;
    max-height: 100%;

    .center-title {
      font-size: 16px;
      font-weight: 600;
      color: #101828;
      margin-bottom: 10px;
      line-height: 24px;
    }

    .center-description {
      font-size: 13px;
      font-weight: 400;
      color: #313234;
      line-height: 20px;
    }
  }

  .body-image-panel {
    height: fit-content;
    overflow: hidden;
    display: flex;
    align-items: flex-start;
  }
}
</style>
